import { useCallback, useState } from 'react';

// Interface for button text state (accessible by AI)
export interface ButtonTextState {
  button1Text: string;
  button2Text: string;
  button3Text: string;
}

// Interface for AI button control functions
export interface PotionUIControls {
  updateButtonText: (buttonId: 'button1' | 'button2' | 'button3', text: string) => void;
  activateButton1: () => void;
  activateButton2: () => void;
  activateButton3: () => void;
  deactivateAllButtons: () => void;
  getButtonTexts: () => ButtonTextState;
  getActiveButton: () => string | null;
}

/**
 * Hook for managing PotionUI button controls that can be used by AI
 * This separates the business logic from the UI component
 */
export const usePotionUIControls = () => {
  const [activeButton, setActiveButton] = useState<string | null>(null);
  const [buttonTexts, setButtonTexts] = useState<ButtonTextState>({
    button1Text: '',
    button2Text: '', 
    button3Text: ''
  });

  // AI Control Functions - these can be called by AI to manage the game progression
  const updateButtonText = useCallback((buttonId: 'button1' | 'button2' | 'button3', text: string) => {
    setButtonTexts(prev => ({
      ...prev,
      [`${buttonId}Text`]: text
    }));
  }, []);

  const activateButton1 = useCallback(() => {
    setActiveButton('Button1');
  }, []);

  const activateButton2 = useCallback(() => {
    // Deactivate button 1 and activate button 2
    setActiveButton('Button2');
  }, []);

  const activateButton3 = useCallback(() => {
    // Deactivate button 2 and activate button 3
    setActiveButton('Button3');
  }, []);

  const deactivateAllButtons = useCallback(() => {
    setActiveButton(null);
  }, []);

  const getButtonTexts = useCallback((): ButtonTextState => {
    return buttonTexts;
  }, [buttonTexts]);

  const getActiveButton = useCallback((): string | null => {
    return activeButton;
  }, [activeButton]);

  // Manual button toggle for direct user interaction
  const handleButtonToggle = useCallback((buttonId: string, lastClickTime: React.MutableRefObject<number>) => {
    const now = Date.now();
    // Throttle clicks to prevent rapid state changes (300ms minimum between clicks)
    if (now - lastClickTime.current < 300) {
      console.log('Click throttled');
      return;
    }
    lastClickTime.current = now;

    if (activeButton === buttonId) {
      // Deactivate current button
      setActiveButton(null);
    } else {
      // Activate new button (this automatically deactivates others)
      setActiveButton(buttonId);
    }
  }, [activeButton]);

  // Create the controls object that can be passed to AI
  const controls: PotionUIControls = {
    updateButtonText,
    activateButton1,
    activateButton2,
    activateButton3,
    deactivateAllButtons,
    getButtonTexts,
    getActiveButton
  };

  return {
    // State for UI components
    activeButton,
    buttonTexts,
    
    // Functions for UI interaction
    handleButtonToggle,
    
    // Controls object for AI integration
    controls
  };
};
